// src/services/api.ts
import axios, { AxiosInstance, AxiosError } from "axios";

/* --------------------------
   Environment & token keys
   -------------------------- */
const API_URL = process.env.REACT_APP_API_URL || "https://api.vastavik.org";
const API_VERSION = process.env.REACT_APP_API_VERSION || "v1";

const TOKEN_KEY = "vastavik_access_token";
const REFRESH_TOKEN_KEY = "vastavik_refresh_token";

/* --------------------------
   Shared types
   -------------------------- */

export interface ApiError {
  message: string;
  errors?: Array<{ field: string; message: string }>;
}

export interface AuthResponse {
  success: boolean;
  data: {
    user: {
      id: string;
      email: string;
      firstName?: string;
      lastName?: string;
      tier?: string;
    };
    tokens: {
      accessToken: string;
      refreshToken: string;
      tokenType: string;
      expiresIn: number;
    };
    storageQuota?: {
      tier: string;
      quotaBytes: number;
      usedBytes: number;
      availableBytes: number;
    };
  };
}

export interface UserProfile {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  tier?: string;
  ownerId?: string;   // Creator Key — prefix of the user's Vastavik IDs
}

export interface MediaItem {
  id: string;
  filename: string;
  originalFilename: string;
  mimeType: string;
  size: number;
  ownerKey?: string;
  verificationStatus?: string;
  createdAt: string;
  updatedAt?: string;
  protectionType?: string;
  protectionApplied?: boolean;
}

export interface UploadProgress {
  progress: number;
  status: string;
  message?: string;
}

// Normalized job status returned by GET /media/job/:jobId (see backend
// sendJobResponse). Mirrors what the iOS/Android apps poll after a 202.
export interface JobStatus {
  jobId: string;
  status: string; // PROCESSING | UPLOADED | REGISTERING | COMPLETED | CONFIRMED | REJECTED | FAILED
  progress: number;
  statusMessage?: string;
  isRejected?: boolean;
  rejectionCode?: string | null;
  rejectionReason?: string | null;
  canVerifyWithOriginal?: boolean;
  // True while an uploaded original is being matched against this media.
  verifyingOriginal?: boolean;
  ownerKey?: string | null;
  mediaId?: string | null;
  blockchainRegistered?: boolean;
  blockchainTxId?: string | null;
  nftTokenId?: string | null;
}


/* --------------------------
   New Types for Voiceover / Audio (add near MediaItem definition)
   -------------------------- */
export interface VoiceoverStatusData {
  mediaId: string;
  voiceoverGenerated: boolean;
  voiceoverPending: boolean;
  voiceoverUrl?: string;
  voiceoverGender?: string;
  voiceoverDuration?: number;
  voiceoverError?: string;
  nftTokenId?: string;
}

export interface VoiceoverStatusResponse {
  success: boolean;
  data?: VoiceoverStatusData;
  error?: string;
}

export interface AudioMetadata {
  duration?: number;
  bitrate?: number;
  sampleRate?: number;
  channels?: number;
  codec?: string;
  format?: string;
  fileSize?: number;
}

/* --------------------------
   Extend MediaItem (update the existing interface you already have)
   -------------------------- */
export interface MediaItem {
  id: string;
  filename: string;
  originalFilename: string;
  mimeType: string;
  size: number;
  ownerKey?: string;
  verificationStatus?: string;
  createdAt: string;
  updatedAt?: string;
  protectionType?: string;
  protectionApplied?: boolean;
  // audio fields
  isAudio?: boolean;
  audioMetadata?: AudioMetadata;
  voiceoverUrl?: string;
  voiceoverGenerated?: boolean;
  voiceoverGender?: string;
  voiceoverPending?: boolean;
  voiceoverDuration?: number;
}



/* --------------------------
   Token helpers (localStorage)
   -------------------------- */
const getAccessToken = (): string | null => localStorage.getItem(TOKEN_KEY);
const setAccessToken = (token: string): void => localStorage.setItem(TOKEN_KEY, token);
const getRefreshToken = (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY);
const setRefreshToken = (token: string): void => localStorage.setItem(REFRESH_TOKEN_KEY, token);
const clearTokens = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

/* --------------------------
   ApiService class (auth, uploads, user)
   -------------------------- */
class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${API_URL}/api/${API_VERSION}`,
      timeout: 120000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // attach Authorization header when token exists
    this.api.interceptors.request.use(
      (config) => {
        const token = getAccessToken();
        if (token) {
          config.headers = config.headers || {};
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // response interceptor: attempt refresh on 401
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = (error.config as any) || {};
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const refreshToken = getRefreshToken();
            if (!refreshToken) throw new Error("No refresh token");

            const resp = await axios.post(`${API_URL}/api/${API_VERSION}/auth/refresh`, {
              refreshToken,
            });

            if (resp.data?.success && resp.data.data?.tokens) {
              const tokens = resp.data.data.tokens;
              setAccessToken(tokens.accessToken);
              if (tokens.refreshToken) setRefreshToken(tokens.refreshToken);

              // update header and retry original request
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`;
              return this.api(originalRequest);
            }
          } catch (refreshErr) {
            clearTokens();
            // redirect to login page (app may handle route changes differently)
            if (typeof window !== "undefined") window.location.href = "/login";
            return Promise.reject(refreshErr);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /* Auth */
  isAuthenticated(): boolean {
    return !!getAccessToken();
  }
  clearTokens(): void {
    clearTokens();
  }

  async login(credentials: { email: string; password: string }): Promise<AuthResponse> {
    const response = await this.api.post("/auth/login", credentials);
    if (response.data?.success && response.data.data?.tokens) {
      setAccessToken(response.data.data.tokens.accessToken);
      setRefreshToken(response.data.data.tokens.refreshToken);
    } else {
    }
    return response.data;
  }

  async register(data: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
    ownerId?: string;    // ✅ NEW
    country?: string;    // ✅ NEW
  }): Promise<any> {
    const response = await this.api.post("/auth/register", data);
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await this.api.post("/auth/logout");
    } finally {
      clearTokens();
    }
  }

  // ✅ NEW METHOD - add after register()
  async checkOwnerIdAvailability(
    ownerId: string,
    firstName?: string,
    lastName?: string,
    email?: string
  ): Promise<{ success: boolean; data?: { available: boolean; ownerId: string; suggestions?: string[] }; error?: string }> {
    const response = await this.api.post("/user/check-owner-id", { ownerId, firstName, lastName, email });
    return response.data;
  }

  async refreshToken(): Promise<void> {
    const refreshToken = getRefreshToken();
    if (!refreshToken) throw new Error("No refresh token available");
    const response = await this.api.post("/auth/refresh", { refreshToken });
    if (response.data?.success && response.data.data?.tokens) {
      setAccessToken(response.data.data.tokens.accessToken);
      if (response.data.data.tokens.refreshToken) setRefreshToken(response.data.data.tokens.refreshToken);
    }
  }

  /* Profile */
  async getProfile(): Promise<UserProfile> {
    const response = await this.api.get("/user/profile");
    return response.data.data?.user || response.data.data;
  }
  async updateProfile(data: { firstName?: string; lastName?: string; phone?: string }): Promise<UserProfile> {
    const response = await this.api.put("/user/profile", data);
    return response.data.data;
  }
  async changePassword(data: { currentPassword: string; newPassword: string }): Promise<void> {
    await this.api.post("/user/change-password", data);
  }

  /* Uploads (protection-first) */
  async uploadMediaWithProtection(formData: FormData, onProgress?: (progress: number) => void): Promise<any> {
    const response = await this.api.post("/media/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 180000,
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data;
  }

  // Backwards-compatible wrapper
  async uploadMedia(file: File, onProgress?: (progress: number) => void): Promise<any> {
    const formData = new FormData();
    formData.append("files", file);
    return this.uploadMediaWithProtection(formData, onProgress);
  }

  async getUploadStatus(jobId: string): Promise<UploadProgress> {
    // Correct endpoint is /media/job/:jobId (the old /media/upload-status/:jobId
    // never existed and always 404'd). Kept for backwards compatibility.
    const response = await this.api.get(`/media/job/${jobId}`);
    return response.data.data;
  }

  // Poll a single upload job's status — matches how the apps track processing
  // after the fast 202. Returns the full normalized job record.
  async getJobStatus(jobId: string): Promise<JobStatus> {
    const response = await this.api.get(`/media/job/${jobId}`);
    return response.data.data as JobStatus;
  }

  async getUserMedia(page: number = 1, limit: number = 20): Promise<MediaItem[]> {
    const response = await this.api.get("/media/user-media", { params: { page, limit } });
    return response.data.data?.media || response.data.data || [];
  }

  async getUploadHistory(page: number = 1, limit: number = 20): Promise<any> {
    const response = await this.api.get("/user/upload-history", { params: { page, limit } });
    if (response.data?.success && response.data.data) return response.data.data;
    return response.data;
  }

  async getMediaDetails(mediaId: string): Promise<MediaItem> {
    const response = await this.api.get(`/media/${mediaId}`);
    return response.data.data;
  }

  // Download the designed QR / Vastavik ID badge attached to a media as a Blob.
  // Fetched via the authenticated client so the object URL can trigger a save.
  async getMediaQrBlob(mediaId: string): Promise<Blob> {
    const response = await this.api.get(`/media/${mediaId}/qr.png`, { responseType: "blob" });
    return response.data as Blob;
  }

  async getMediaBadgeBlob(mediaId: string): Promise<Blob> {
    const response = await this.api.get(`/media/${mediaId}/badge.png`, { responseType: "blob" });
    return response.data as Blob;
  }

  // ── Support tickets ──────────────────────────────────────────
  async getSupportCategories(): Promise<any[]> {
    const response = await this.api.get("/support/categories");
    return response.data?.data || [];
  }
  async createSupportTicket(body: {
    categorySlug?: string; categoryId?: string; subject: string; message: string; relatedMediaId?: string;
  }): Promise<any> {
    const response = await this.api.post("/support/tickets", body);
    return response.data?.data;
  }
  async getSupportTickets(): Promise<any[]> {
    const response = await this.api.get("/support/tickets");
    return response.data?.data || [];
  }
  async getSupportTicket(id: string): Promise<any> {
    const response = await this.api.get(`/support/tickets/${id}`);
    return response.data?.data;
  }
  async replySupportTicket(id: string, message: string): Promise<any> {
    const response = await this.api.post(`/support/tickets/${id}/messages`, { message });
    return response.data?.data;
  }

  async deleteMedia(mediaId: string): Promise<void> {
    await this.api.delete(`/media/${mediaId}`);
  }

  /* ── Raw-vs-edit verification flow (v-next) ─────────────────
     For media flagged as edited/AI-enhanced: the creator uploads the raw
     original, the platform proves the edit derives from it, and only then
     registers. Manual review is USER-requested — nothing auto-queues. */

  async startMatchRequest(mediaId: string): Promise<any> {
    const response = await this.api.post(`/media/${mediaId}/match-request`);
    return response.data.data;
  }

  async getRawUploadUrl(mediaId: string, contentType: string): Promise<{ uploadUrl: string; s3Key: string }> {
    const response = await this.api.post(`/media/${mediaId}/raw-upload-url`, { contentType });
    return response.data.data;
  }

  /** PUT the raw file straight to S3 (presigned) — bytes never touch the API. */
  async uploadRawToS3(uploadUrl: string, file: File, onProgress?: (pct: number) => void): Promise<void> {
    await axios.put(uploadUrl, file, {
      headers: { "Content-Type": file.type || "application/octet-stream" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
  }

  async confirmRawUploaded(mediaId: string): Promise<any> {
    const response = await this.api.post(`/media/${mediaId}/raw-uploaded`);
    return response.data.data;
  }

  async getMatchVerification(mediaId: string): Promise<any | null> {
    try {
      const response = await this.api.get(`/media/${mediaId}/verification`);
      return response.data.data;
    } catch (e: any) {
      if (e?.response?.status === 404) return null;
      throw e;
    }
  }

  async requestManualReview(mediaId: string, message?: string): Promise<any> {
    const response = await this.api.post(`/media/${mediaId}/request-review`, { message });
    return response.data.data;
  }

  // Creator has no untouched original — send a reason to support.
  async submitNoOriginal(mediaId: string, reason: string): Promise<any> {
    const response = await this.api.post(`/media/${mediaId}/no-original`, { reason });
    return response.data.data;
  }

  async getStorageQuota(): Promise<any> {
    const response = await this.api.get("/user/storage-quota");
    if (response.data?.success && response.data.data) return response.data.data;
    return response.data;
  }

  /* Public verification / media queries (no auth) */
  async verifyMedia(qrData: string): Promise<any> {
    const response = await axios.post(`${API_URL}/api/${API_VERSION}/verify/qr`, { qrData });
    return response.data;
  }

  async getMediaByOwnerKey(ownerKey: string): Promise<any> {
    const response = await axios.get(`${API_URL}/api/${API_VERSION}/media/owner-key/${ownerKey}`);
    return response.data;
  }

  
/* --------------------------
   ApiService additions (add methods to ApiService class)
   -------------------------- */

  /**
   * Check voiceover status for a media item
   * GET /media/voiceover-status/:mediaId
   */
   async  checkVoiceoverStatus(mediaId: string): Promise<VoiceoverStatusResponse> {
    const response = await this.api.get(`/media/voiceover-status/${encodeURIComponent(mediaId)}`);
    return response.data;
  }

  /**
   * Regenerate voiceover for a media item
   * POST /media/regenerate-voiceover/:mediaId
   */
    async   regenerateVoiceover(mediaId: string): Promise<VoiceoverStatusResponse> {
    const response = await this.api.post(`/media/regenerate-voiceover/${encodeURIComponent(mediaId)}`);
    return response.data;
  }

  async verifyEmailOTP(email: string, otp: string): Promise<any> {
    const response = await this.api.post("/auth/verify-email-otp", { email, otp });
    return response.data;
  }

  // ✅ NEW: Resend verification OTP (same as iOS APIClient.resendVerificationOTP)
  // POST /api/v1/auth/resend-verification-otp
  async resendVerificationOTP(email: string): Promise<any> {
    const response = await this.api.post("/auth/resend-verification-otp", { email });
    return response.data;
  }

  /* ──────────────────────────────────────────────────────────────
     Billing · Subscription · Storage (matches the mobile apps)
     ────────────────────────────────────────────────────────────── */

  // Plans + storage pricing resolved in the user's currency
  async getBillingConfig(): Promise<any> {
    const response = await this.api.get("/billing/config");
    return response.data?.data ?? response.data;
  }

  async getPlans(): Promise<any[]> {
    const response = await this.api.get("/billing/plans");
    return response.data?.data ?? response.data ?? [];
  }

  // Request a custom plan (apps' "Need something bigger?" flow) — POST /billing/custom-plan/request
  async requestCustomPlan(payload: {
    requestedStorageGb?: number;
    isUnlimited?: boolean;
    message?: string;
    contactEmail?: string;
  }): Promise<any> {
    const response = await this.api.post("/billing/custom-plan/request", payload);
    return response.data?.data ?? response.data;
  }

  // Current subscription (or FREE)
  async getSubscription(): Promise<any> {
    const response = await this.api.get("/billing/subscription");
    return response.data?.data ?? response.data;
  }

  // Returns Stripe params { clientSecret, publishableKey, ephemeralKey, customerId, ... }
  async createSubscriptionCheckout(planName: string, billingCycle: "monthly" | "yearly"): Promise<any> {
    const response = await this.api.post("/billing/subscribe", { planName, billingCycle });
    return response.data?.data ?? response.data;
  }

  async cancelSubscription(immediately = false, reason?: string): Promise<any> {
    const response = await this.api.post("/billing/subscribe/cancel", { immediately, reason });
    return response.data;
  }

  // Storage usage + active purchases
  async getStorageDetails(): Promise<any> {
    const response = await this.api.get("/billing/storage");
    return response.data?.data ?? response.data;
  }

  // Over-quota enforcement: { state: 'ok' | 'warning' | 'locked', usedBytes, quotaBytes, ... }
  async getStorageEnforcement(): Promise<any> {
    const response = await this.api.get("/billing/storage/enforcement");
    return response.data?.data ?? response.data;
  }

  // Returns Stripe params for buying `units` of storage
  async createStorageCheckout(units: number): Promise<any> {
    const response = await this.api.post("/billing/storage/purchase", { units });
    return response.data?.data ?? response.data;
  }

  async cancelStoragePurchase(purchaseId: string, immediately = false): Promise<any> {
    const response = await this.api.post("/billing/storage/cancel", { purchaseId, immediately });
    return response.data;
  }

  async getTransactions(): Promise<any[]> {
    const response = await this.api.get("/billing/transactions");
    return response.data?.data ?? response.data ?? [];
  }

  /* Account — matches the apps' DELETE /user/account */
  async deleteAccount(): Promise<void> {
    await this.api.delete("/user/account");
  }
}

/* Export singleton */
export const apiService = new ApiService();
export default apiService;

/* -------------------------------------------------------------------------- */
/*                           Folder / File types + helpers                    */
/* -------------------------------------------------------------------------- */

export interface BackendFile {
  id: string;
  filename?: string;
  originalFilename?: string;
  displayName?: string;
  mimeType?: string;
  size?: number;
  url?: string;
  ownerKey?: string; // maps to folder.creatorKey
  verificationStatus?: string;
  protectionType?: string;
  protectionApplied?: boolean;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

export interface BackendFolder {
  id: string;
  name: string;
  parentId: string | null;
  createdAt: number;
  creatorKeys?: string[];
  children?: BackendFolder[];
  files?: BackendFile[]; // filled client-side after mapping external files
}

export interface FoldersResponse {
  folders: BackendFolder[];
  unassignedFiles: BackendFile[];
}

/* --------- Helpers ---------- */

export function getFileBaseUrl(): string {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const win = typeof window !== "undefined" ? (window as any) : undefined;
    const fromWindow = win?.__API_BASE__;
    if (fromWindow && typeof fromWindow === "string" && fromWindow.length) {
      return String(fromWindow).replace(/\/api\/?$/, "");
    }

    const envBase =
      (typeof process !== "undefined" && (process as any).env && ((process as any).env.BASE || (process as any).env.BASE_URL)) ||
      "";
    if (envBase && typeof envBase === "string" && envBase.length) {
      return String(envBase).replace(/\/api\/?$/, "");
    }
  } catch (e) {
    // noop
  }
  return "https://uiapi.vastavik.org";
}

export function normalizeFiles<T extends BackendFile>(files: T[]): T[] {
  const fileBase = getFileBaseUrl();
  return files.map((f) => {
    const candidate = f.url ?? f.originalFilename ?? f.filename ?? f.id ?? "";
    let url = "";
    if (typeof candidate === "string" && candidate.length) {
      const trimmed = candidate.trim();
      if (/^https?:\/\//i.test(trimmed)) url = trimmed;
      else if (trimmed.startsWith("/")) url = `${fileBase}${trimmed}`;
      else url = `${fileBase}/${trimmed}`;
    }
    const displayName = f.displayName || f.filename || f.originalFilename || f.id;
    return { ...f, displayName, url };
  });
}

/* -------------------------------------------------------------------------- */
/*                           Axios instance for folder endpoints             */
/* -------------------------------------------------------------------------- */

const resolvedBase =
  (typeof window !== "undefined" && (window as any).__API_BASE__) ||
  (typeof process !== "undefined" && (process as any).env && ((process as any).env.BASE || (process as any).env.BASE_URL)) ||
  "https://uiapi.vastavik.org/api";

const folderApi = axios.create({
  baseURL: String(resolvedBase),
  timeout: 60000,
});

folderApi.interceptors.request.use((cfg) => {
  try {
    const token = typeof window !== "undefined" ? (localStorage.getItem(TOKEN_KEY) as string | null) : null;
    if (token) {
      cfg.headers = cfg.headers || {};
      if (!cfg.headers.Authorization && !(cfg.headers as any).authorization) {
        (cfg.headers as any).Authorization = `Bearer ${token}`;
      }
    }
  } catch (e) {
    // ignore
  }
  return cfg;
});

/* -------------------------------------------------------------------------- */
/*                           Folder API functions                             */
/* -------------------------------------------------------------------------- */

export async function fetchUserFolders(): Promise<FoldersResponse> {
  const res = await folderApi.get("/folders/getallfolders");
  const data = res.data?.data as FoldersResponse | undefined;
  if (!data) return { folders: [], unassignedFiles: [] };

  // normalize files (if any) returned by backend (DB-only files)
  return {
    folders: (data.folders || []).map((folder) => ({
      ...folder,
      files: normalizeFiles(folder.files || []),
      children: (folder.children || []).map((c) => ({ ...c, files: normalizeFiles(c.files || []) })),
    })),
    unassignedFiles: normalizeFiles(data.unassignedFiles || []),
  };
}

export async function createFolderApi(name: string, parentId?: string | null, creatorKey?: string | null) {
  const res = await folderApi.post("/folders/createFolder", { name, parentId: parentId ?? null, creatorKey: creatorKey ?? null });
  return res.data?.data as BackendFolder;
}

export async function updateFolderApi(id: string, updates: { name?: string; creatorKey?: string | null }) {
  const res = await folderApi.put(`/folders/updateFolder/${id}`, updates);
  return res.data?.data as BackendFolder;
}

export async function deleteFolderApi(id: string) {
  await folderApi.delete(`/folders/deletefolder/${id}`);
}

export async function moveFolderApi(id: string, newParentId?: string | null) {
  const res = await folderApi.put(`/folders/movefolder/${id}/move`, { newParentId: newParentId ?? null });
  return res.data?.data as BackendFolder;
}


  
/* -------------------------------------------------------------------------- */
/*                           File move / mapping APIs                         */
/* -------------------------------------------------------------------------- */

/**
 * moveFileApi
 * - We DO NOT try to import files or verify DB existence here.
 * - We send the ownerKey (creatorKey) that the backend should store on the target folder.
 * - fileId is a UI reference (keeps request readable). Backend will decide behavior:
 *   - If a DB file exists with that id, backend can move it.
 *   - Otherwise backend will treat ownerKey as creatorKey and assign it to the folder.
 */
export async function moveFileApi(
  ownerKey: string,
  targetFolderId?: string | null
) {
  const res = await folderApi.put(
    `/files/movefile/${encodeURIComponent(ownerKey)}/move`,
    {
      targetFolderId: targetFolderId ?? null,
    }
  );
  return res.data?.data ?? res.data;
}


/**
 * moveMultipleFilesApi
 * - Accepts list of fileIds (UI references) — commonly these will be external IDs or ownerKeys.
 * - Backend will handle logic; frontend simply forwards the selection and the target folder.
 */
export async function moveMultipleFilesApi(
  ownerKeys: string[],
  targetFolderId?: string | null
) {
  if (!Array.isArray(ownerKeys) || ownerKeys.length === 0) {
    throw new Error("ownerKeys array is required");
  }

  const res = await folderApi.post(`/files/move-multiplefiles`, {
    fileIds: ownerKeys, // backend treats these as ownerKeys
    targetFolderId: targetFolderId ?? null,
  });

  return res.data?.data ?? res.data;
}

/**
 * moveAllFilesApi
 * - Delegates to backend. This operation is DB-only (moves files assigned to a source folder).
 */
export async function moveAllFilesApi(sourceFolderId: string, targetFolderId?: string | null) {
  if (!sourceFolderId) throw new Error("sourceFolderId is required");
  const res = await folderApi.post(`/files/move-all-files`, {
    sourceFolderId,
    targetFolderId: targetFolderId ?? null,
  });
  return res.data?.data ?? res.data;
}

/* optional export of the axios instance */
export { folderApi as folderApiInstance };